'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { AppIcon } from '@impacta/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { FEATURE_AUTH_ENABLED } from '@/lib/features';
import CartDrawer from '@/components/CartDrawer';
import HeaderSearch from '@/components/HeaderSearch';

const navLinks = [
  { label: 'Actividades', href: '/actividades' },
  { label: 'Talleristas', href: '/talleristas' },
  { label: 'Nosotros', href: '/nosotros' },
  { label: 'Ayuda', href: '/ayuda' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isMobileAccountOpen, setIsMobileAccountOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, signOut, getUserRole } = useAuth();
  const { itemCount, openDrawer } = useCart();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router?.push('/homepage');
      router?.refresh();
    } catch (err) {
      // ignore
    }
  };

  const role = mounted && user ? getUserRole() : null;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-header' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link
            href="/homepage"
            className={`flex-shrink-0 transition-all duration-1000 ${
              mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <AppLogo src="/assets/images/logo-turquesa.png" size={36} width={120} height={44} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`text-sm font-semibold transition-colors duration-200 ${
                  pathname === link?.href ? 'text-[#00A19B]' : 'text-[#373A36] hover:text-[#00A19B]'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Cart */}
            <button
              onClick={openDrawer}
              className="relative p-2 rounded-full text-[#6b6e68] hover:text-[#00A19B] hover:bg-[#e6f7f6] transition-all duration-200"
              aria-label="Ver carrito"
            >
              <AppIcon name="ShoppingBagIcon" size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#00A19B] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center leading-none">
                  {itemCount}
                </span>
              )}
            </button>

            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-full text-[#6b6e68] hover:text-[#00A19B] hover:bg-[#e6f7f6] transition-all duration-200"
              aria-label="Buscar actividades"
            >
              <AppIcon name="MagnifyingGlassIcon" size={20} />
            </button>

            {FEATURE_AUTH_ENABLED && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                    isAccountDropdownOpen
                      ? 'bg-[#e6f7f6] text-[#00A19B]'
                      : 'text-[#373A36] hover:text-[#00A19B] hover:bg-[#e6f7f6]'
                  }`}
                  aria-haspopup="true"
                  aria-expanded={isAccountDropdownOpen}
                >
                  <AppIcon name="UserCircleIcon" size={18} />
                  {user ? 'Mi Cuenta' : 'Iniciar sesión'}
                  <AppIcon
                    name="ChevronDownIcon"
                    size={14}
                    className={`transition-transform duration-200 ${
                      isAccountDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Dropdown Menu */}
                <div
                  className={`absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-lg border border-[#E8E8E4] overflow-hidden transition-all duration-200 origin-top-right ${
                    isAccountDropdownOpen
                      ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
                      : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
                  }`}
                >
                  <div className="py-1">
                    {user ? (
                      <>
                        <Link
                          href="/cuenta/perfil"
                          onClick={() => setIsAccountDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#373A36] hover:bg-[#e6f7f6] hover:text-[#00A19B] transition-colors duration-150"
                        >
                          <AppIcon name="UserCircleIcon" size={16} />
                          Mi Cuenta
                        </Link>
                        <Link
                          href="/cuenta/mis-reservas"
                          onClick={() => setIsAccountDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#373A36] hover:bg-[#e6f7f6] hover:text-[#00A19B] transition-colors duration-150"
                        >
                          <AppIcon name="CalendarIcon" size={16} />
                          Mis Reservas
                        </Link>
                        {role === 'tallerista' && (
                          <Link
                            href="/dashboard-tallerista"
                            onClick={() => setIsAccountDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#612166] hover:bg-[#f0e8f0] transition-colors duration-150"
                          >
                            <AppIcon name="AcademicCapIcon" size={16} />
                            Mi Cuenta Tallerista
                          </Link>
                        )}
                        {role === 'admin' && (
                          <Link
                            href="/admin"
                            onClick={() => setIsAccountDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#1E1E2E] hover:bg-gray-100 transition-colors duration-150"
                          >
                            <AppIcon name="Squares2X2Icon" size={16} />
                            Mi Cuenta Admin
                          </Link>
                        )}
                        <div className="border-t border-[#E8E8E4] my-1" />
                        <button
                          onClick={() => {
                            setIsAccountDropdownOpen(false);
                            handleSignOut();
                          }}
                          className="flex items-center gap-2.5 w-full px-4 py-3 text-sm font-semibold text-[#373A36] hover:bg-[#e6f7f6] hover:text-[#00A19B] transition-colors duration-150"
                        >
                          <AppIcon name="ArrowLeftOnRectangleIcon" size={16} />
                          Cerrar sesión
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          href="/login"
                          onClick={() => setIsAccountDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#373A36] hover:bg-[#e6f7f6] hover:text-[#00A19B] transition-colors duration-150"
                        >
                          <AppIcon name="ArrowRightOnRectangleIcon" size={16} />
                          Iniciar sesión
                        </Link>
                        <Link
                          href="/registro"
                          onClick={() => setIsAccountDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#373A36] hover:bg-[#e6f7f6] hover:text-[#00A19B] transition-colors duration-150"
                        >
                          <AppIcon name="UserPlusIcon" size={16} />
                          Registrarse
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Gift CTA */}
            <Link
              href="/regalar"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-200 bg-[#E0DF00] text-[#373A36] hover:bg-[#00A19B] hover:text-white"
            >
              <AppIcon name="GiftIcon" size={16} />
              Regalar
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="lg:hidden flex items-center gap-1">
            {/* Cart (mobile) */}
            <button
              onClick={openDrawer}
              className="relative p-2 rounded-full text-[#373A36] hover:bg-[#e6f7f6] transition-colors"
              aria-label="Ver carrito"
            >
              <AppIcon name="ShoppingBagIcon" size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#00A19B] text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center leading-none">
                  {itemCount}
                </span>
              )}
            </button>

            {/* Search (mobile) */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-full text-[#373A36] hover:bg-[#e6f7f6] transition-colors"
              aria-label="Buscar actividades"
            >
              <AppIcon name="MagnifyingGlassIcon" size={22} />
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className="p-2 rounded-full text-[#373A36] hover:bg-[#e6f7f6] transition-colors"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-label={isMobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              <AppIcon name={isMobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E8E8E4] px-6 py-4 space-y-1">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setIsMobileOpen(false)}
              className={`block py-3 text-sm font-semibold border-b border-[#E8E8E4] last:border-0 transition-colors ${
                pathname === link?.href ? 'text-[#00A19B]' : 'text-[#373A36]'
              }`}
            >
              {link?.label}
            </Link>
          ))}

          {FEATURE_AUTH_ENABLED && (
            /* Mobile Account Accordion */
            <div className="border-b border-[#E8E8E4]">
              <button
                onClick={() => setIsMobileAccountOpen(!isMobileAccountOpen)}
                className="flex items-center justify-between w-full py-3 text-sm font-semibold text-[#373A36]"
              >
                <span className="flex items-center gap-2">
                  <AppIcon name="UserCircleIcon" size={16} />
                  {user ? 'Mi Cuenta' : 'Iniciar sesión'}
                </span>
                <AppIcon
                  name="ChevronDownIcon"
                  size={14}
                  className={`transition-transform duration-200 ${
                    isMobileAccountOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isMobileAccountOpen && (
                <div className="pb-2 pl-6 space-y-1">
                  {user ? (
                    <>
                      <Link
                        href="/cuenta/perfil"
                        onClick={() => {
                          setIsMobileOpen(false);
                          setIsMobileAccountOpen(false);
                        }}
                        className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#373A36] hover:text-[#00A19B] transition-colors"
                      >
                        <AppIcon name="UserCircleIcon" size={15} />
                        Mi Cuenta
                      </Link>
                      <Link
                        href="/cuenta/mis-reservas"
                        onClick={() => {
                          setIsMobileOpen(false);
                          setIsMobileAccountOpen(false);
                        }}
                        className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#373A36] hover:text-[#00A19B] transition-colors"
                      >
                        <AppIcon name="CalendarIcon" size={15} />
                        Mis Reservas
                      </Link>
                      {role === 'tallerista' && (
                        <Link
                          href="/dashboard-tallerista"
                          onClick={() => {
                            setIsMobileOpen(false);
                            setIsMobileAccountOpen(false);
                          }}
                          className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#612166] hover:text-[#612166]/80 transition-colors"
                        >
                          <AppIcon name="AcademicCapIcon" size={15} />
                          Mi Cuenta Tallerista
                        </Link>
                      )}
                      {role === 'admin' && (
                        <Link
                          href="/admin"
                          onClick={() => {
                            setIsMobileOpen(false);
                            setIsMobileAccountOpen(false);
                          }}
                          className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#1E1E2E] hover:text-gray-600 transition-colors"
                        >
                          <AppIcon name="Squares2X2Icon" size={15} />
                          Mi Cuenta Admin
                        </Link>
                      )}
                      <div className="border-t border-[#E8E8E4] my-1" />
                      <button
                        onClick={() => {
                          setIsMobileOpen(false);
                          setIsMobileAccountOpen(false);
                          handleSignOut();
                        }}
                        className="flex items-center gap-2 w-full py-2.5 text-sm font-semibold text-[#373A36] hover:text-[#00A19B] transition-colors text-left"
                      >
                        <AppIcon name="ArrowLeftOnRectangleIcon" size={15} />
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/login"
                        onClick={() => {
                          setIsMobileOpen(false);
                          setIsMobileAccountOpen(false);
                        }}
                        className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#373A36] hover:text-[#00A19B] transition-colors"
                      >
                        <AppIcon name="ArrowRightOnRectangleIcon" size={15} />
                        Iniciar sesión
                      </Link>
                      <Link
                        href="/registro"
                        onClick={() => {
                          setIsMobileOpen(false);
                          setIsMobileAccountOpen(false);
                        }}
                        className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#373A36] hover:text-[#00A19B] transition-colors"
                      >
                        <AppIcon name="UserPlusIcon" size={15} />
                        Registrarse
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pt-3">
            <Link
              href="/regalar"
              onClick={() => setIsMobileOpen(false)}
              className="btn-cta w-full justify-center"
            >
              <AppIcon name="GiftIcon" size={16} />
              Regalar experiencia
            </Link>
          </div>
        </div>
      )}
      <CartDrawer />
      <HeaderSearch open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </header>
  );
}
